import * as joint from '@joint/core'

/**
 * Export options for diagram export
 */
export interface ExportOptions {
  /** Background color for the export (default: white) */
  backgroundColor?: string
  /** Padding around the diagram in pixels (default: 20) */
  padding?: number
  /** Scale factor (default: 2 for higher resolution) */
  scale?: number
}

/**
 * Export the diagram to SVG format
 */
export function exportToSVG(
  paper: joint.dia.Paper,
  options: ExportOptions = {}
): string {
  const { backgroundColor = '#ffffff', padding = 20 } = options

  // Get the content bounding box
  const bbox = paper.model.getBBox()
  if (!bbox) {
    throw new Error('No content to export')
  }

  // Clone the SVG element
  const svgElement = paper.svg.cloneNode(true) as SVGSVGElement

  // Calculate new dimensions with padding
  const width = bbox.width + padding * 2
  const height = bbox.height + padding * 2

  // Set SVG dimensions and viewBox
  svgElement.setAttribute('width', String(width))
  svgElement.setAttribute('height', String(height))
  svgElement.setAttribute('viewBox', `${bbox.x - padding} ${bbox.y - padding} ${width} ${height}`)

  // Add background
  const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  bgRect.setAttribute('x', String(bbox.x - padding))
  bgRect.setAttribute('y', String(bbox.y - padding))
  bgRect.setAttribute('width', String(width))
  bgRect.setAttribute('height', String(height))
  bgRect.setAttribute('fill', backgroundColor)
  svgElement.insertBefore(bgRect, svgElement.firstChild)

  // Serialize to string
  const serializer = new XMLSerializer()
  const svgString = serializer.serializeToString(svgElement)

  return svgString
}

/**
 * Export the diagram to PNG format
 */
export async function exportToPNG(
  paper: joint.dia.Paper,
  options: ExportOptions = {}
): Promise<Blob> {
  const { backgroundColor = '#ffffff', padding = 20, scale = 2 } = options

  // Get SVG first
  const svgString = exportToSVG(paper, { backgroundColor, padding })

  // Get dimensions
  const bbox = paper.model.getBBox()
  if (!bbox) {
    throw new Error('No content to export')
  }

  const width = (bbox.width + padding * 2) * scale
  const height = (bbox.height + padding * 2) * scale

  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Could not get canvas context')
  }

  // Create image from SVG
  const img = new Image()
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)

  return new Promise((resolve, reject) => {
    img.onload = () => {
      // Fill background
      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, width, height)

      // Draw image
      ctx.drawImage(img, 0, 0, width, height)

      // Convert to blob
      canvas.toBlob(
        blob => {
          URL.revokeObjectURL(url)
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to create PNG blob'))
          }
        },
        'image/png',
        1.0
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG for PNG conversion'))
    }

    img.src = url
  })
}

/**
 * Download a file with the given content
 */
export function downloadFile(content: Blob | string, filename: string, mimeType?: string): void {
  let blob: Blob
  if (typeof content === 'string') {
    blob = new Blob([content], { type: mimeType || 'text/plain' })
  } else {
    blob = content
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Export and download diagram as SVG
 */
export function downloadSVG(
  paper: joint.dia.Paper,
  filename: string = 'diagram.svg',
  options: ExportOptions = {}
): void {
  const svgString = exportToSVG(paper, options)
  downloadFile(svgString, filename, 'image/svg+xml')
}

/**
 * Export and download diagram as PNG
 */
export async function downloadPNG(
  paper: joint.dia.Paper,
  filename: string = 'diagram.png',
  options: ExportOptions = {}
): Promise<void> {
  const blob = await exportToPNG(paper, options)
  downloadFile(blob, filename, 'image/png')
}
